import {
  issueViewerPrimaryQueryKey,
  preheatIssueViewerPrimary,
} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {useCachePreheatTask, useCachePreheatTasks} from '@github-ui/preheat-issues-manager/use-cache-preheat'
import {getQueryClient} from '@github-ui/query-client'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useEffect, useMemo} from 'react'

import {issueViewerSecondaryViewQueryOptions} from '../components/fetch-issue-viewer-secondary-view'
import {preheatIssueViewerSecondaryComponents} from '../components/IssueViewer'

type UseCachePreheatIssueViewerProps = {
  owner: string
  repo: string
  number: number
  source: string
  enabled?: boolean
}

type UseCachePreheatIssueViewerQueriesProps = {
  issues: Array<{
    owner: string
    repo: string
    number: number
  }>
  source: string
}

export function useCachePreheatIssueViewer({
  owner,
  repo,
  number,
  source,
  enabled = true,
}: UseCachePreheatIssueViewerProps) {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})

  const secondaryQueryOptions = useMemo(
    () =>
      issueViewerSecondaryViewQueryOptions({
        owner,
        repo,
        number,
        markAsRead: false,
        sendAnalyticsEvent,
        preheat: true,
        preheatSource: source,
      }),
    [number, owner, repo, sendAnalyticsEvent, source],
  )
  const preheatSecondaryQuery = useCallback(
    () => getQueryClient().prefetchQuery(secondaryQueryOptions),
    [secondaryQueryOptions],
  )
  const primaryQueryKey = useMemo(() => issueViewerPrimaryQueryKey({owner, repo, number}), [owner, repo, number])
  const preheatPrimaryQuery = useCallback(
    () => preheatIssueViewerPrimary({owner, repo, number, source}),
    [owner, repo, number, source],
  )

  useEffect(() => {
    if (!enabled) return
    void preheatIssueViewerSecondaryComponents()
  }, [enabled])

  useCachePreheatTask({
    key: JSON.stringify(primaryQueryKey),
    task: preheatPrimaryQuery,
    enabled,
    highPriority: true,
  })

  useCachePreheatTask({
    key: JSON.stringify(secondaryQueryOptions.queryKey),
    task: preheatSecondaryQuery,
    enabled,
  })
}

export function useCachePreheatIssueViewerQueries({issues, source}: UseCachePreheatIssueViewerQueriesProps) {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})

  useEffect(() => {
    if (issues.length === 0) return
    void preheatIssueViewerSecondaryComponents()
  }, [issues.length])

  const secondaryPreheatTasks = useMemo(
    () =>
      issues.map(({owner, repo, number}) => {
        const options = issueViewerSecondaryViewQueryOptions({
          owner,
          repo,
          number,
          markAsRead: false,
          sendAnalyticsEvent,
          preheat: true,
          preheatSource: source,
        })
        return {
          key: JSON.stringify(options.queryKey),
          task: () => getQueryClient().prefetchQuery(options),
        }
      }),
    [issues, sendAnalyticsEvent, source],
  )

  const primaryPreheatTasks = useMemo(
    () =>
      issues.map(issue => ({
        key: JSON.stringify(issueViewerPrimaryQueryKey(issue)),
        task: () => preheatIssueViewerPrimary({...issue, source}),
        highPriority: true,
      })),
    [issues, source],
  )

  useCachePreheatTasks({tasks: primaryPreheatTasks})
  useCachePreheatTasks({tasks: secondaryPreheatTasks})
}
